/**
 * "Available · Free until 3:00 PM / Work hours: 08:00 AM - 05:00 PM /
 *  10:24 PM local time · Same time zone as you".
 *
 * Three independent facts from three independent sources — the live presence
 * feed, the calendar, and the person's `users.timezone`. Each line is dropped
 * whole when its source has nothing to say, so the block shrinks to just the
 * presence label in a workspace with no calendar data.
 */
import { Clock } from 'lucide-react';
import { cn } from '@cyphex/ui';
import { PersonPresenceDot } from '@/features/presence/components/PersonPresenceDot';
import { PresenceLine } from '@/features/presence/components/PresenceLine';
import { usePersonSnapshot } from '@/features/presence/hooks/usePresence';
import type { PersonAvailability } from '../api/peopleApi';
import { useNow } from '../hooks/useNow';
import { calendarLine, workHoursLine } from '../lib/availability';
import { localTimeFor, viewerTimeZone } from '../lib/localTime';

export interface ProfileAvailabilityProps {
  userId: string;
  /** The person's own zone, from `users.timezone`. */
  timezone: string | null;
  availability: PersonAvailability | null;
  className?: string;
}

export function ProfileAvailability({
  userId,
  timezone,
  availability,
  className,
}: ProfileAvailabilityProps) {
  const now = useNow();
  const snapshot = usePersonSnapshot(userId);
  const state = snapshot?.effectiveState ?? 'offline';
  // "🎧 Focus time" — free text the person set themselves, when they set one.
  const custom = snapshot?.status;
  const status =
    custom === null || custom === undefined || custom.text === ''
      ? null
      : `${custom.emoji === null ? '' : `${custom.emoji} `}${custom.text}`;
  const local = localTimeFor(timezone, now);
  const calendar = calendarLine(availability, viewerTimeZone(), now);
  const hours = workHoursLine(availability, now);

  return (
    <section className={cn('space-y-1.5', className)} aria-label="Availability">
      {/* Dot + label, exactly as the thread header states it — NOT
          `PresenceIndicator showLabel`, whose extra glyph renders "⊗ ✕ Offline"
          and reads as two different claims about the same fact. */}
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <PersonPresenceDot userId={userId} size="sm" ringClassName="ring-popover" />
        {/* The state in words comes from PresenceLine, not from a bare label
            lookup: this block sits directly beneath the card's own PresenceLine,
            and reading "Away" under "Away · Last seen 15 minutes ago" is two
            different answers to one question. */}
        <PresenceLine
          as="span"
          state={state}
          lastActivityAt={snapshot?.resolved === true ? snapshot.lastActivityAt : null}
          className="mt-0 text-md font-medium text-secondary-foreground"
        />
        {calendar === null ? null : (
          <span className="text-md text-muted-foreground">· {calendar}</span>
        )}
        {status === null ? null : <span className="text-sm text-muted-foreground">{status}</span>}
      </p>

      {hours === null ? null : (
        <p className="pl-5 text-sm text-muted-foreground">Work hours: {hours}</p>
      )}

      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Clock size={14} className="shrink-0" aria-hidden />
        <span>
          {local.clock} local time
          {local.sameAsViewer ? ' · Same time zone as you' : ''}
        </span>
      </p>
    </section>
  );
}
